import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { STYLES, ASPECT_RATIOS, Style, AspectRatio } from './types';
import { generateImageAsync } from '../../services/ideogramService';
import { toast } from '../ui/use-toast';
import { Loader2, Wand2, Upload, Link } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

interface UploadRemixDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartGenerating: () => void;
}

export const UploadRemixDialog: React.FC<UploadRemixDialogProps> = ({
  open,
  onOpenChange,
  onStartGenerating,
}) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'url'>('upload');
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState<Style>('AUTO');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('ASPECT_16_9');
  const [imageWeight, setImageWeight] = useState(50);
  const [imageUrl, setImageUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    maxFiles: 1,
    multiple: false
  });

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prompt",
        variant: "destructive",
      });
      return;
    }

    const sourceImageUrl = activeTab === 'upload' ? uploadedImage : imageUrl;
    if (!sourceImageUrl) {
      toast({
        title: "Error",
        description: "Please provide an image to remix",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      await generateImageAsync(prompt, aspectRatio, style, {
        sourceImageUrl,
        imageWeight
      });
      
      onOpenChange(false);
      onStartGenerating();
      
      // Reset form
      setPrompt('');
      setImageUrl('');
      setUploadedImage(null);
      setActiveTab('upload');
      
      toast({
        title: "Success",
        description: "Image generation started. Please wait while we process your request.",
      });
    } catch (error) {
      console.error('Error generating image:', error);
      toast({
        title: "Error",
        description: "Failed to generate image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Remix Image</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-6">
          {/* Image Source */}
          <div className="space-y-4">
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'upload' | 'url')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="upload" className="flex items-center">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </TabsTrigger>
                <TabsTrigger value="url" className="flex items-center">
                  <Link className="w-4 h-4 mr-2" />
                  URL
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="upload" className="mt-4">
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                    ${isDragActive ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary'}
                    ${uploadedImage ? 'border-primary' : ''}`}
                >
                  <input {...getInputProps()} />
                  {uploadedImage ? (
                    <img
                      src={uploadedImage}
                      alt="Selected file for remixing"
                      className="max-h-[300px] mx-auto rounded-lg"
                    />
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-8 h-8 mx-auto text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {isDragActive
                          ? "Drop the image here"
                          : "Drag and drop an image here, or click to select"}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="url" className="mt-4">
                <Input
                  placeholder="Enter image URL"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                />
                {imageUrl && (
                  <div className="mt-4">
                    <img
                      src={imageUrl}
                      alt="Selected from URL for remixing"
                      className="max-h-[300px] mx-auto rounded-lg"
                      onError={() => {
                        toast({
                          title: "Error",
                          description: "Failed to load image from URL",
                          variant: "destructive",
                        });
                      }}
                    />
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Remix Controls */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Prompt</label>
              <Textarea
                placeholder="Enter your prompt..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Style</label>
              <Select value={style} onValueChange={(value) => setStyle(value as Style)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select style" />
                </SelectTrigger>
                <SelectContent>
                  {STYLES.map((style) => (
                    <SelectItem key={style} value={style}>
                      {style}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Aspect Ratio</label>
              <Select value={aspectRatio} onValueChange={(value) => setAspectRatio(value as AspectRatio)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select aspect ratio" />
                </SelectTrigger>
                <SelectContent>
                  {ASPECT_RATIOS.map((ratio) => (
                    <SelectItem key={ratio} value={ratio}>
                      {ratio.replace('ASPECT_', '').replace('_', ':')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Image Weight: {imageWeight}%</label>
              <input
                type="range"
                min="0"
                max="100"
                value={imageWeight}
                onChange={(e) => setImageWeight(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Remixing...
              </>
            ) : (
              <>
                <Wand2 className="mr-2 h-4 w-4" />
                Remix Image
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
